
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { addressTitle } from '@shared/listing';
import { Button, Card, Container, Eyebrow, Heading, Row, Spacer, Stack, Text } from '@client/shared/design';
import { Stepper } from './Stepper.tsx';
import { StepBasics } from './StepBasics.tsx';
import { StepAvailability } from './StepAvailability.tsx';
import { StepAccess, type AgentOption } from './StepAccess.tsx';
import { useEditListingForm } from './use-edit-listing-form.ts';
import { useGroupAgents } from './use-group-agents.ts';
import {
  fetchListingForEdit,
  listingForEditKey,
  type EditableListing,
} from './edit-listing-mutation.ts';
import styles from './NewListing.module.css';

export interface EditListingProps {
  listingId?: string;
  agents?: readonly AgentOption[];
  onSaved?: (listingId: string) => void;
  onCancel?: () => void;
}

export function editListingIdFromPath(pathname: string): string | null {
  const match = /\/listings\/([^/?#]+)\/edit/.exec(pathname);
  const id = match?.[1];
  if (!id || id === 'new') return null;
  return decodeURIComponent(id);
}

export function EditListing({ listingId, agents, onSaved, onCancel }: EditListingProps) {
  const id =
    listingId ??
    (typeof window !== 'undefined' ? editListingIdFromPath(window.location.pathname) : null);

  const query = useQuery({
    queryKey: listingForEditKey(id ?? '∅'),
    queryFn: ({ signal }) => fetchListingForEdit(id!, signal),
    enabled: Boolean(id),
  });

  if (!id) return <Shell><StateCard title="Listing not found" body="This listing doesn’t exist or isn’t available to your group." /></Shell>;

  if (query.isPending) return <Shell><StateCard title="Loading…" body="Loading the listing details." /></Shell>;
  if (query.isError)
    return (
      <Shell>
        <StateCard
          title="We couldn’t load this listing"
          body="Something went wrong loading this listing. Try again or refresh if it keeps happening."
          action={<Button type="button" variant="secondary" onClick={() => void query.refetch()}>Try again</Button>}
        />
      </Shell>
    );
  if (!query.data.listing)
    return <Shell><StateCard title="Listing not found" body="This listing doesn’t exist or isn’t available to your group." /></Shell>;

  return <EditListingForm listing={query.data.listing} agents={agents} onSaved={onSaved} onCancel={onCancel} />;
}

function EditListingForm({
  listing,
  agents: agentsProp,
  onSaved,
  onCancel,
}: {
  listing: EditableListing;
  agents?: readonly AgentOption[];
  onSaved?: (listingId: string) => void;
  onCancel?: () => void;
}) {
  const loaded = useGroupAgents();
  const agents = agentsProp ?? loaded.agents;
  const wizard = useEditListingForm(listing, onSaved);
  const { form, step, stepIndex, isFirstStep, isLastStep, next, back, goTo, stepError, submitError, submitting } =
    wizard;

  return (
    <Shell
      title={addressTitle(form.state.values.address)}
      intro="Update the listing’s details. Anyone on the notify list will be emailed."
    >
      <div className={styles.layout}>
        <aside className={styles.rail}>
          <Stepper activeIndex={stepIndex} onJump={goTo} />
        </aside>

        <Card pad="lg" spine className={styles.panel} as="section" aria-live="polite">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (isLastStep) void form.handleSubmit();
              else next();
            }}
            noValidate
          >
            <Stack gap={7}>
              <div className={styles.stepPanel} data-step={step}>
                {step === 'basics' && <StepBasics form={form} />}
                {step === 'availability' && <StepAvailability form={form} showBlocks={false} />}
                {step === 'access' && <StepAccess form={form} agents={agents} />}
              </div>

              {(stepError || submitError) && (
                <p className={styles.formError} role="alert">
                  <ErrorMark />
                  {submitError ?? stepError}
                </p>
              )}

              <Row gap={3} align="center" className={styles.footer}>
                {isFirstStep ? (
                  onCancel && (
                    <Button type="button" variant="ghost" onClick={onCancel}>
                      Cancel
                    </Button>
                  )
                ) : (
                  <Button type="button" variant="secondary" onClick={back} disabled={submitting}>
                    Back
                  </Button>
                )}
                <Spacer />
                <Text size="xs" tone="muted" className={styles.stepCount}>
                  Step {stepIndex + 1} of 3
                </Text>
                {isLastStep ? (
                  <Button type="submit" variant="primary" loading={submitting}>
                    Save changes
                  </Button>
                ) : (
                  <Button type="submit" variant="primary">
                    Continue
                  </Button>
                )}
              </Row>
            </Stack>
          </form>
        </Card>
      </div>
    </Shell>
  );
}

function Shell({
  title = 'Edit listing',
  intro,
  children,
}: {
  title?: string;
  intro?: string;
  children: ReactNode;
}) {
  return (
    <Container size="default">
      <div className={styles.page}>
        <Stack gap={2} className={styles.head}>
          <Eyebrow>Edit listing</Eyebrow>
          <Heading level={1} className={styles.headTitle}>{title}</Heading>
          {intro && <Text tone="soft" className={styles.headIntro}>{intro}</Text>}
        </Stack>
        {children}
      </div>
    </Container>
  );
}

function StateCard({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <Card pad="lg" as="section">
      <Stack gap={3} align="center">
        <Heading level={3} as="h2">
          {title}
        </Heading>
        <Text size="sm" tone="soft">
          {body}
        </Text>
        {action}
      </Stack>
    </Card>
  );
}

function ErrorMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" className={styles.errorMark}>
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 4.5v4.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="8" cy="11.4" r="0.85" fill="currentColor" />
    </svg>
  );
}

export default EditListing;
